import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { WebUser, Role } from './types';

// Authentication.
//
// Production uses the OIDC Authorization Code + PKCE flow against Zitadel. In
// local dev and for the test harness `VITE_AUTH_STUB` is set, which issues a
// self-describing stub JWT (validated by the API in dev mode) so the full
// frontend can be exercised without a configured Zitadel WEB client + Actions.

const STORAGE_KEY = 'lucille.session';

export interface Session {
  token: string;
  user: WebUser;
}

interface AuthContextValue {
  user: WebUser | null;
  token: string | null;
  login: (email: string, _password: string) => Promise<WebUser>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function base64url(input: string): string {
  return btoa(unescape(encodeURIComponent(input)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function makeStubToken(sub: string, email: string, role: Role, locationIds: string[]): string {
  const header = base64url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64url(
    JSON.stringify({ sub, email, role, location_ids: locationIds, iat: Math.floor(Date.now() / 1000) }),
  );
  return `${header}.${payload}.stub`;
}

// Seeded dev identities that mirror apps/api/prisma/seed.ts so the dev-login
// flow exercises the real permissioning model.
const DEV_USERS: Record<string, WebUser> = {
  'admin@lucille.example': {
    sub: 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb',
    email: 'admin@lucille.example',
    name: 'Admin',
    role: 'super_admin',
    locationIds: [],
  },
  'alice@lucille.example': {
    sub: 'cccccccc-cccc-4ccc-8ccc-cccccccccccc',
    email: 'alice@lucille.example',
    name: 'Alice',
    role: 'chef',
    locationIds: ['44444444-4444-4444-8444-444444444444', '55555555-5555-4555-8555-555555555555'],
  },
  'bob@lucille.example': {
    sub: 'dddddddd-dddd-4ddd-8ddd-dddddddddddd',
    email: 'bob@lucille.example',
    name: 'Bob',
    role: 'chef',
    locationIds: ['66666666-6666-4666-8666-666666666666'],
  },
};

function readStoredSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export const stubLogin = (email: string): WebUser => {
  const known = DEV_USERS[email.toLowerCase()];
  if (known) return known;
  // Unknown email falls back to a chef with no locations (empty catalog view).
  return {
    sub: `user-${email.toLowerCase()}`,
    email,
    name: email.split('@')[0],
    role: 'chef',
    locationIds: [],
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(readStoredSession);

  const login = useCallback(async (email: string, _password: string): Promise<WebUser> => {
    const user = stubLogin(email);
    const token = makeStubToken(user.sub, user.email, user.role, user.locationIds);
    const next = { token, user };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSession(next);
    return user;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user: session?.user ?? null, token: session?.token ?? null, login, logout }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
